import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useQueryState } from "nuqs";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { PartnerCodeFilterField, PartnerCodeFilterTag, PartnerCodeFilterOperator } from "@/components/admin/partner-codes/PartnerCodesColumnFilters";
import type { PartnerCodeSortField } from "@/components/admin/partner-codes/PartnerCodesTable";

type PartnerCode = Tables<"partner_codes">;
type PartnerCodeInsert = TablesInsert<"partner_codes">;
type PartnerCodeUpdate = TablesUpdate<"partner_codes">;

const STORAGE_KEY = "admin-partner-codes-filters";
const SORT_STORAGE_KEY = "admin-partner-codes-sort";
const SHOW_SYSTEM_STORAGE_KEY = "admin-partner-codes-show-system";
const ITEMS_PER_PAGE = 20;

const getInitialPartnerCodeSort = (): { field: PartnerCodeSortField; direction: "asc" | "desc" } => {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(SORT_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall through
      }
    }
  }
  return { field: "created_at", direction: "desc" };
};

// Helper to compare values with operators
const compareWithOperator = (value: number, filterValue: number, operator: PartnerCodeFilterOperator): boolean => {
  switch (operator) {
    case "equals": return value === filterValue;
    case ">=": return value >= filterValue;
    case "<=": return value <= filterValue;
    case ">": return value > filterValue;
    case "<": return value < filterValue;
    default: return value === filterValue;
  }
};

// Helper to compare strings with operators
const compareStringWithOperator = (value: string, filterValue: string, operator: PartnerCodeFilterOperator): boolean => {
  switch (operator) {
    case "equals": return value.toLowerCase() === filterValue.toLowerCase();
    case "contains": return value.toLowerCase().includes(filterValue.toLowerCase());
    default: return value.toLowerCase().includes(filterValue.toLowerCase());
  }
};

export const useAdminPartnerCodes = () => {
  const queryClient = useQueryClient();

  // URL sync for filters
  const [filtersParam, setFiltersParam] = useQueryState("filters");

  // Filter state
  const [filterTags, setFilterTags] = useState<PartnerCodeFilterTag[]>(() => {
    // Try URL first, then localStorage
    if (filtersParam) {
      try {
        return JSON.parse(filtersParam);
      } catch {
        // Fall through to localStorage
      }
    }
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {
          return [];
        }
      }
    }
    return [];
  });

  // Sorting state with persistence
  const initialSort = getInitialPartnerCodeSort();
  const [sortField, setSortField] = useState<PartnerCodeSortField>(initialSort.field);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(initialSort.direction);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Show system-generated codes toggle (persisted)
  const [showSystemGenerated, setShowSystemGeneratedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SHOW_SYSTEM_STORAGE_KEY) === "true";
  });
  const setShowSystemGenerated = useCallback((value: boolean) => {
    setShowSystemGeneratedState(value);
    if (typeof window !== "undefined") {
      localStorage.setItem(SHOW_SYSTEM_STORAGE_KEY, value ? "true" : "false");
    }
    setCurrentPage(1);
  }, []);

  // Persist filters to localStorage and URL
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filterTags));
    if (filterTags.length > 0) {
      setFiltersParam(JSON.stringify(filterTags));
    } else {
      setFiltersParam(null);
    }
  }, [filterTags, setFiltersParam]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterTags]);

  const addFilter = useCallback((field: PartnerCodeFilterField, value: string, operator?: PartnerCodeFilterOperator) => {
    // Check if this exact filter already exists
    const exists = filterTags.some(
      (t) => t.field === field && t.value.toLowerCase() === value.toLowerCase() && t.operator === operator
    );
    if (exists) return;

    const id = `${field}-${value}-${operator || "default"}-${Date.now()}`;
    const label =
      field === "status"
        ? value.charAt(0).toUpperCase() + value.slice(1)
        : field === "type"
          ? value.charAt(0).toUpperCase() + value.slice(1)
          : value;

    setFilterTags((prev) => [...prev, { id, field, value, label, operator }]);
  }, [filterTags]);

  const removeFilter = useCallback((id: string) => {
    setFilterTags((prev) => prev.filter((tag) => tag.id !== id));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilterTags([]);
  }, []);

  const handleSort = useCallback((field: PartnerCodeSortField) => {
    let newDirection: "asc" | "desc";
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      newDirection = "desc";
    }
    setSortField(field);
    setSortDirection(newDirection);
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ field, direction: newDirection }));
    setCurrentPage(1);
  }, [sortField, sortDirection]);

  // Fetch data
  const { data: partnerCodes, isLoading } = useQuery({
    queryKey: ["admin-partner-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_codes")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as PartnerCode[];
    },
  });

  // Apply filters, sorting, and pagination
  const processedData = useMemo(() => {
    if (!partnerCodes) return { codes: [], totalCount: 0, totalPages: 0, systemHiddenCount: 0 };

    // Hide system-generated codes unless toggle is on
    const systemHiddenCount = showSystemGenerated
      ? 0
      : partnerCodes.filter((c) => c.is_system_generated).length;
    let filtered = showSystemGenerated
      ? [...partnerCodes]
      : partnerCodes.filter((c) => !c.is_system_generated);

    // Apply filters (OR within same field, AND across fields)
    if (filterTags.length > 0) {
      const filtersByField = filterTags.reduce(
        (acc, tag) => {
          if (!acc[tag.field]) acc[tag.field] = [];
          acc[tag.field].push({ value: tag.value.toLowerCase(), operator: tag.operator });
          return acc;
        },
        {} as Record<PartnerCodeFilterField, { value: string; operator?: PartnerCodeFilterOperator }[]>
      );

      filtered = filtered.filter((code) => {
        return Object.entries(filtersByField).every(([field, filters]) => {
          switch (field as PartnerCodeFilterField) {
            case "status":
              return filters.some((f) =>
                f.value === "active" ? code.is_active : !code.is_active
              );
            case "type":
              return filters.some((f) => code.discount_type?.toLowerCase() === f.value);
            case "partner":
              return filters.some((f) =>
                compareStringWithOperator(code.partner_name, f.value, f.operator || "contains")
              );
            case "code":
              return filters.some((f) =>
                compareStringWithOperator(code.code, f.value, f.operator || "contains")
              );
            case "uses":
              return filters.some((f) =>
                compareWithOperator(code.current_uses || 0, parseInt(f.value), f.operator || "equals")
              );
            case "creditsValue":
              return filters.some((f) =>
                compareWithOperator(code.credits_value || 0, parseInt(f.value), f.operator || "equals")
              );
            default:
              return true;
          }
        });
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "code":
          comparison = a.code.localeCompare(b.code);
          break;
        case "partner_name":
          comparison = a.partner_name.localeCompare(b.partner_name);
          break;
        case "discount_type":
          comparison = (a.discount_type || "").localeCompare(b.discount_type || "");
          break;
        case "current_uses":
          comparison = (a.current_uses || 0) - (b.current_uses || 0);
          break;
        case "valid_until":
          const aDate = a.valid_until ? new Date(a.valid_until).getTime() : 0;
          const bDate = b.valid_until ? new Date(b.valid_until).getTime() : 0;
          comparison = aDate - bDate;
          break;
        case "created_at":
        default:
          const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
          comparison = aCreated - bCreated;
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    // Apply pagination
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedCodes = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return { codes: paginatedCodes, totalCount, totalPages, systemHiddenCount };
  }, [partnerCodes, filterTags, sortField, sortDirection, currentPage, showSystemGenerated]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (newCode: PartnerCodeInsert) => {
      const { data, error } = await supabase
        .from("partner_codes")
        .insert(newCode)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-partner-codes"] });
      queryClient.invalidateQueries({ queryKey: ["partner-codes-stats-cache"] });
      toast({
        title: "Success",
        description: "Partner code created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PartnerCodeUpdate }) => {
      const { data, error } = await supabase
        .from("partner_codes")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-partner-codes"] });
      queryClient.invalidateQueries({ queryKey: ["partner-codes-stats-cache"] });
      toast({
        title: "Success",
        description: "Partner code updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // First, delete all redemptions for this partner code
      const { error: redemptionsError } = await supabase
        .from("partner_code_redemptions")
        .delete()
        .eq("partner_code_id", id);

      if (redemptionsError) throw redemptionsError;

      // Then delete the partner code
      const { error } = await supabase.from("partner_codes").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-partner-codes"] });
      queryClient.invalidateQueries({ queryKey: ["partner-codes-stats-cache"] });
      toast({
        title: "Success",
        description: "Partner code deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    // Data
    partnerCodes: processedData.codes,
    allPartnerCodes: partnerCodes,
    totalCount: processedData.totalCount,
    totalPages: processedData.totalPages,
    systemHiddenCount: processedData.systemHiddenCount,
    showSystemGenerated,
    setShowSystemGenerated,
    isLoading,

    // Filters
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,

    // Sorting
    sortField,
    sortDirection,
    handleSort,

    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage: ITEMS_PER_PAGE,

    // Mutations
    createPartnerCode: createMutation.mutate,
    updatePartnerCode: updateMutation.mutate,
    deletePartnerCode: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
